import { useEffect } from 'react';
import { Routes, Route } from "react-router-dom";
import { MsalProvider, useMsal } from '@azure/msal-react';
import { EventType } from '@azure/msal-browser';
import { PageLayout } from './components/Main/PageLayout';
import { QuizMain } from './pages/quiz/QuizMain';
import { HandbookGrid } from './pages/profile/HandbookGrid';
import { b2cPolicies, protectedResources } from './authConfig';
import { compareIssuingPolicy } from './utils/claimUtils';
import './styles/App.css';
import Homepage from './pages/quiz/pages/Homepage/homepage'
import { useNavigate } from 'react-router-dom';
import { ConfigProvider } from 'antd';
import axios from 'axios';

const Pages = () => {

    const navigate = useNavigate();
    const cleanLocalStorage = () => {
        localStorage.removeItem('loginToken')
        localStorage.removeItem('quiz-step1-page1')
        localStorage.removeItem('quiz-step2-page1')
        localStorage.removeItem('reset')
        localStorage.removeItem('selected-workplace-hazard')
        localStorage.removeItem('logoUrl');
    }
    /**
     * useMsal is hook that returns the PublicClientApplication instance,
     * an array of all accounts currently signed in and an inProgress value
     * that tells you what msal is currently doing. For more, visit:
     * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/hooks.md
     */
    const { instance } = useMsal();
    useEffect(() => {
        const callbackId = instance.addEventCallback((event) => {
            if (
                (event.eventType === EventType.LOGIN_SUCCESS || event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) &&
                event.payload.account
            ) {
                cleanLocalStorage()
                //SET LOGIN TOKEN IN LOCAL STORAGE
                if (event?.payload && event?.payload?.idToken) {
                    let loginToken = JSON.stringify(event?.payload?.idToken)
                    localStorage.setItem('loginToken', loginToken)
                }

                /**
                 * For the purpose of setting an active account for UI update, we want to consider only the auth
                 * response resulting from SUSI flow. "tfp" claim in the id token tells us the policy (NOTE: legacy
                 * policies may use "acr" instead of "tfp"). To learn more about B2C tokens, visit:
                 * https://docs.microsoft.com/en-us/azure/active-directory-b2c/tokens-overview
                 */
                if (compareIssuingPolicy(event.payload.idTokenClaims, b2cPolicies.names.editProfile)) {
                    // retrieve the account from initial sing-in to the app
                    const originalSignInAccount = instance
                        .getAllAccounts()
                        .find(
                            (account) =>
                                account.idTokenClaims.oid === event.payload.idTokenClaims.oid &&
                                account.idTokenClaims.sub === event.payload.idTokenClaims.sub &&
                                compareIssuingPolicy(account.idTokenClaims, b2cPolicies.names.signUpSignIn)
                        );

                    let signUpSignInFlowRequest = {
                        authority: b2cPolicies.authorities.signUpSignIn.authority,
                        account: originalSignInAccount,
                    };

                    // silently login again with the signUpSignIn policy
                    instance.ssoSilent(signUpSignInFlowRequest);
                }

                /**
                 * Below we are checking if the user is returning from the reset password flow.
                 * If so, we will ask the user to reauthenticate with their new password.
                 * If you do not want this behavior and prefer your users to stay signed in instead,
                 * you can replace the code below with the same pattern used for handling the return from
                 * profile edit flow
                 */
                if (compareIssuingPolicy(event.payload.idTokenClaims, b2cPolicies.names.forgotPassword)) {
                    let signUpSignInFlowRequest = {
                        authority: b2cPolicies.authorities.signUpSignIn.authority,
                        scopes: [
                            ...protectedResources.apiTodoList.scopes.read,
                            ...protectedResources.apiTodoList.scopes.write,
                        ],
                    };
                    instance.loginRedirect(signUpSignInFlowRequest);
                }
            }

            if (event.eventType === EventType.LOGIN_FAILURE) {
                // Check for forgot password error
                // Learn more about AAD error codes at https://docs.microsoft.com/en-us/azure/active-directory/develop/reference-aadsts-error-codes
                if (event.error && event.error.errorMessage.includes('AADB2C90118')) {
                    const resetPasswordRequest = {
                        authority: b2cPolicies.authorities.forgotPassword.authority,
                        scopes: [],
                    };
                    instance.loginRedirect(resetPasswordRequest);
                }
            }
        });

        return () => {
            if (callbackId) {
                instance.removeEventCallback(callbackId);
            }
        };
        // eslint-disable-next-line
    }, [instance]);

    useEffect(() => {
        const fetchLogo = async () => {
            try {
                const token = JSON.parse(localStorage.getItem('loginToken'));
                const endpoint = `${process.env.REACT_APP_BASE_URL}/user/companylogo`;

                const response = await axios({
                    method: 'GET',
                    url: endpoint,
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                })

                if (response.data && response.data.message && response.status === 200) {
                    localStorage.setItem('logoUrl', JSON.stringify(response?.data?.message))
                }
            } catch (error) {
                console.error('Failed to fetch logo', error);
            }
        };

        fetchLogo();
    }, [JSON.parse(localStorage.getItem('loginToken'))]);

    return (
        <Routes>
            <Route path="/safety-orientation-handbook/" element={<Homepage nextPage={() => navigate(`/safety-orientation-handbook/quiz`)} />} />
            <Route path="/safety-orientation-handbook/quiz" element={<QuizMain />} />
            <Route path="/safety-orientation-handbook/quiz/:handbookId" element={<QuizMain />} />
            <Route path="/safety-orientation-handbook/profile" element={<HandbookGrid />} />
        </Routes>
    );
};

/**
 * msal-react is built on the React context API and all parts of your app that require authentication must be
 * wrapped in the MsalProvider component. You will first need to initialize an instance of PublicClientApplication
 * then pass this to MsalProvider as a prop. All components underneath MsalProvider will have access to the
 * PublicClientApplication instance via context as well as all hooks and components provided by msal-react. For more, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/getting-started.md
 */
const App = ({ instance }) => {
    return (
        <MsalProvider instance={instance}>
            <ConfigProvider theme={{ hashed: false }}>
                <PageLayout>
                    <Pages />
                </PageLayout>
            </ConfigProvider>
        </MsalProvider>
    );
};

export default App;
